/**
 * Public surface of `src/lib/api/`.
 *
 * `core.ts` owns the authenticated send + on-401-refresh-and-retry recipe
 * (`apiFetchRaw`, `apiFetchWithRefresh`, `tryRefreshToken`) plus the
 * underlying refresh-state coordination and primitives.
 *
 * `responseShapes.ts` owns caller-preference return-shape variants over
 * `apiFetchRaw` (`apiFetch<T>`, `apiStatus`, `apiFetchWithStatus`,
 * `apiLive`) and UI alert formatting (`showErrorAlert`).
 *
 * `server.ts` owns `requireAuth` / `requireAuthAction` and the server-side
 * access-token readers. Import those from `@/lib/api/server` only — this
 * barrel is compiled for client components and must not pull the server authentication SDK.
 * `authResponses.ts` is the canonical wire shape for 401 responses.
 * `errors.ts` owns the auth error classes.
 *
 * `WingmanClient` imports `apiFetchWithRefresh` from this module.
 *
 * See `docs/adr/0002-consolidate-api-module.md` for the rationale.
 */

export {
  apiFetchRaw,
  apiFetchWithRefresh,
  tryRefreshToken,
  _resetAuthState,
  type ApiFetchRawOptions,
  type AccessTokenOrAccessor,
  type HttpMethod,
} from "./core";

export {
  apiFetch,
  apiLive,
  apiStatus,
  apiFetchWithStatus,
  showErrorAlert,
  TRIAL_LIMIT_EVENT,
  type ApiFetchOptions,
  type ApiStatusResult,
  type ApiResponseWithStatus,
  type HandleErrorAlert,
} from "./responseShapes";

export {
  NOT_AUTHORIZED_ERROR,
  NOT_AUTHORIZED_EVENT,
  notAuthorizedBody,
  notifyNotAuthorized,
  parseNotAuthorized,
  resetNotAuthorizedCooldown,
  type NotAuthorizedBody,
  type NotAuthorizedDetail,
} from "./notAuthorized";

export {
  classifyAuthErrorBody,
  notAuthenticatedResponse,
  sessionExpiredResponse,
  type AuthErrorBody,
  type AuthErrorCode,
} from "./authResponses";

export {
  NoSessionError,
  RefreshTokenError,
  SessionExpiredError,
  TerminalAuthError,
  isRefreshTokenError,
} from "./errors";
